/*
  Video reel, for sections/cb-video-reel.liquid.

  - Nothing downloads until the section is within ~300 px of the screen. Then the posters load
    (data-cb-src / data-cb-srcset). A video's file loads only when it is about to play.
  - One video at a time: it plays muted while at least a third of the section is on screen, and the
    next one starts when it ends (the row scrolls sideways to it, never the page).
  - Tap a video: if it isn't playing, it plays; if it plays muted, the sound turns on (for the
    following videos too); if it plays with sound, it pauses.
  - A swipe makes the most visible video the active one.
  - Reduced motion or save-data: nothing plays by itself; tap to play. The ticker stands still (CSS).
  Loaded as a module, so several sections can include it and it still runs once per page.
*/
const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = !!(navigator.connection && navigator.connection.saveData);
const autoplay = !reduce && !saveData;

function showPosters(root) {
  root.querySelectorAll('img[data-cb-reel-poster]').forEach((img) => {
    if (img.dataset.cbSrcset) img.srcset = img.dataset.cbSrcset;
    if (img.dataset.cbSrc) img.src = img.dataset.cbSrc;
    img.removeAttribute('data-cb-reel-poster');
  });
}

function setup(root) {
  if (root.dataset.cbReelReady) return;
  root.dataset.cbReelReady = '1';

  const track = root.querySelector('.cb-reel__track');
  const ticker = root.querySelector('.cb-reel__ticker');
  const toggle = ticker && ticker.querySelector('.cb-reel__ticker-toggle');
  if (toggle) {
    toggle.addEventListener('click', () => {
      const on = !ticker.classList.contains('is-paused');
      ticker.classList.toggle('is-paused', on);
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      toggle.setAttribute('aria-label', on ? 'Play the moving phrases' : 'Pause the moving phrases');
    });
  }

  const cards = track ? Array.from(track.querySelectorAll('.cb-reel__card')) : [];
  let active = 0;
  let inView = false;
  let sound = false;
  let userPaused = false;

  if (!('IntersectionObserver' in window)) {
    showPosters(root);
  } else {
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        near.disconnect();
        showPosters(root);
      },
      { rootMargin: '300px 0px' }
    );
    near.observe(root);
  }
  if (!cards.length) return;

  const videoOf = (card) => card.querySelector('video');
  const hitOf = (card) => card.querySelector('.cb-reel__hit');

  function label(card) {
    const hit = hitOf(card);
    const name = hit.dataset.label || 'video';
    const v = videoOf(card);
    if (v.paused) hit.setAttribute('aria-label', 'Play: ' + name);
    else if (v.muted) hit.setAttribute('aria-label', 'Turn sound on: ' + name);
    else hit.setAttribute('aria-label', 'Pause: ' + name);
  }

  function load(card) {
    const v = videoOf(card);
    if (v.dataset.cbLoaded || !v.dataset.cbSrc) return;
    v.dataset.cbLoaded = '1';
    v.src = v.dataset.cbSrc;
    v.preload = 'auto';
  }

  function pause(card) {
    const v = videoOf(card);
    if (!v.paused) v.pause();
    card.classList.remove('is-playing');
    label(card);
  }

  function play(card) {
    cards.forEach((c) => {
      if (c !== card) {
        pause(c);
        c.classList.remove('is-active');
      }
    });
    active = cards.indexOf(card);
    card.classList.add('is-active');
    load(card);
    const v = videoOf(card);
    v.muted = !sound;
    card.classList.toggle('is-sound', sound);
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(
        () => {
          card.classList.add('is-playing', 'is-started');
          label(card);
        },
        () => {
          // Sound was blocked: fall back to muted.
          if (!v.muted) {
            sound = false;
            v.muted = true;
            card.classList.remove('is-sound');
            v.play().then(() => card.classList.add('is-playing', 'is-started'), () => {});
          }
          label(card);
        }
      );
    } else {
      card.classList.add('is-playing', 'is-started');
      label(card);
    }
  }

  // Scroll the row (not the page) so a card is fully in view.
  function reveal(card) {
    const left = card.offsetLeft - track.offsetLeft;
    const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
    const visible = left >= track.scrollLeft && left + card.offsetWidth <= track.scrollLeft + track.clientWidth;
    if (!visible) track.scrollTo({ left: Math.max(0, left - pad), behavior: reduce ? 'auto' : 'smooth' });
  }

  cards.forEach((card, i) => {
    const v = videoOf(card);
    v.addEventListener('timeupdate', () => {
      if (v.duration) card.style.setProperty('--cb-reel-p', String(Math.min(1, v.currentTime / v.duration)));
    });
    v.addEventListener('ended', () => {
      card.classList.remove('is-playing');
      card.style.setProperty('--cb-reel-p', '0');
      v.currentTime = 0;
      label(card);
      if (!inView || userPaused) return;
      if (!autoplay && !sound) return;
      const next = cards[(i + 1) % cards.length];
      reveal(next);
      play(next);
    });
    hitOf(card).addEventListener('click', () => {
      if (v.paused || cards.indexOf(card) !== active) {
        userPaused = false;
        play(card);
      } else if (v.muted) {
        sound = true;
        v.muted = false;
        card.classList.add('is-sound');
        label(card);
      } else {
        userPaused = true;
        pause(card);
      }
    });
  });

  // After a swipe, the most visible card becomes the active one.
  let settle;
  track.addEventListener(
    'scroll',
    () => {
      clearTimeout(settle);
      settle = setTimeout(() => {
        const mid = track.scrollLeft + track.clientWidth / 2;
        let best = active;
        let bestDist = Infinity;
        cards.forEach((c, i) => {
          const center = c.offsetLeft - track.offsetLeft + c.offsetWidth / 2;
          const d = Math.abs(center - mid);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        const current = cards[active];
        const visible = (c) => {
          const left = c.offsetLeft - track.offsetLeft;
          return left + c.offsetWidth > track.scrollLeft + 8 && left < track.scrollLeft + track.clientWidth - 8;
        };
        if (visible(current)) return;
        if (!videoOf(current).paused && inView && !userPaused) play(cards[best]);
        else active = best;
      }, 160);
    },
    { passive: true }
  );

  if ('IntersectionObserver' in window) {
    const view = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting && entry.intersectionRatio >= 0.33;
          const card = cards[active];
          if (!inView) {
            cards.forEach(pause);
          } else if (autoplay && !userPaused && videoOf(card).paused && !document.hidden) {
            play(card);
          }
        });
      },
      { threshold: [0, 0.33, 0.6] }
    );
    view.observe(track);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) cards.forEach(pause);
    else if (inView && autoplay && !userPaused) play(cards[active]);
  });
}

function scan(scope) {
  (scope || document).querySelectorAll('[data-cb-reel]').forEach(setup);
}

scan(document);
document.addEventListener('shopify:section:load', (event) => scan(event.target));
document.addEventListener('shopify:block:select', (event) => {
  const card = event.target.closest && event.target.closest('.cb-reel__card');
  const track = card && card.parentElement;
  if (card && track) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
});
